import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useEffect, useState, useMemo } from 'react';
import useHoverTargetElement from './hooks/useHoverTargetElement';
import useManageHoverEvents from './hooks/useManageHoverEvents';
import { isVideoElementPaused, isVideoElementPlaying, } from './utils/videoElementPlaybackStates';
import { expandToFillContainerStyle, containerSizingStyles, pausedOverlayWrapperSizingStyles, videoSizingStyles, } from './PlayVideoOnHover.styles';
import { getUrlForPreview } from '../../utils/media/cloudfront-utils';
const PlayVideoOnHover = ({ videoSrc, loadingOverlay, pausedOverlay, skipCdn, muted = true, loop = true, videoElementProps = {}, }) => {
    const cloudFrontPreviewUrl = useMemo(() => getUrlForPreview(!!skipCdn, videoSrc), [videoSrc, skipCdn]);
    const containerRef = useRef(null);
    const videoRef = useRef(null);
    const hoverTargetElement = useHoverTargetElement(containerRef);
    const nextVideoStartTimeRef = useRef(null);
    const attemptToPlayVideoRef = useRef();
    attemptToPlayVideoRef.current = () => {
        const videoElement = videoRef.current;
        if (nextVideoStartTimeRef.current !== null) {
            videoElement.currentTime = nextVideoStartTimeRef.current;
        }
        videoElement.play().catch((error) => {
            console.log('Error while trying to play video on hover -> ', error);
            return;
        });
    };
    const attemptToPauseVideoRef = useRef();
    attemptToPauseVideoRef.current = () => {
        const videoElement = videoRef.current;
        videoElement.pause();
        videoElement.currentTime = 0;
        nextVideoStartTimeRef.current = videoElement.currentTime;
        setIsPlaying(false);
    };
    const [isPlaying, setIsPlaying] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const hasPausedOverlay = Boolean(pausedOverlay);
    const hasHoverOverlay = false;
    const shouldWaitForOverlayTransitionBeforePausing = hasPausedOverlay || hasHoverOverlay;
    useEffect(() => {
        if (!hoverTargetElement)
            return undefined;
        let playbackStartTimeout = null;
        let pauseTimeout = null;
        const cancelTimeouts = () => {
            if (playbackStartTimeout)
                window.clearTimeout(playbackStartTimeout);
            if (pauseTimeout)
                window.clearTimeout(pauseTimeout);
        };
        const attemptToPlayVideoOnHover = () => {
            cancelTimeouts();
            setIsHovering(true);
            if (isVideoElementPaused(videoRef.current)) {
                attemptToPlayVideoRef.current && attemptToPlayVideoRef.current();
            }
        };
        const attemptToPauseVideoOnHoverEnd = () => {
            cancelTimeouts();
            setIsHovering(false);
            if (isVideoElementPlaying(videoRef.current) &&
                shouldWaitForOverlayTransitionBeforePausing) {
                pauseTimeout = window.setTimeout(() => {
                    attemptToPauseVideoRef.current && attemptToPauseVideoRef.current();
                }, 400);
            }
            else {
                attemptToPauseVideoRef.current && attemptToPauseVideoRef.current();
            }
        };
        hoverTargetElement.addEventListener('hvp:hoverStart', attemptToPlayVideoOnHover);
        hoverTargetElement.addEventListener('hvp:hoverEnd', attemptToPauseVideoOnHoverEnd);
        return () => {
            hoverTargetElement.removeEventListener('hvp:hoverStart', attemptToPlayVideoOnHover);
            hoverTargetElement.removeEventListener('hvp:hoverEnd', attemptToPauseVideoOnHoverEnd);
            cancelTimeouts();
        };
    }, [hoverTargetElement, shouldWaitForOverlayTransitionBeforePausing]);
    useManageHoverEvents(hoverTargetElement, false);
    const hasLoadingOverlay = Boolean(loadingOverlay);
    const shouldShowLoadingOverlay = isHovering && !isPlaying;
    const shouldShowPausedOverlay = !isHovering || (isHovering && !isPlaying);
    return (_jsxs("div", { ref: containerRef, style: {
            ...containerSizingStyles,
        }, className: 'iv-absolute iv-top-0 iv-left-0 iv-right-0 iv-bottom-0 iv-w-full iv-h-full', children: [hasPausedOverlay ? (_jsx("div", { style: {
                    ...pausedOverlayWrapperSizingStyles,
                    width: '100%',
                    zIndex: 1,
                    opacity: shouldShowPausedOverlay ? 1 : 0,
                    transition: `opacity 400ms`,
                    pointerEvents: shouldShowPausedOverlay ? 'auto' : 'none',
                }, children: pausedOverlay })) : null, hasLoadingOverlay ? (_jsx("div", { style: {
                    ...expandToFillContainerStyle,
                    zIndex: 2,
                    width: '100%',
                    opacity: shouldShowLoadingOverlay ? 1 : 0,
                    transition: `opacity 400ms 0ms`,
                    pointerEvents: shouldShowLoadingOverlay ? 'auto' : 'none',
                }, children: loadingOverlay })) : null, _jsx("video", { muted: muted, loop: loop, playsInline: true, preload: "none", crossOrigin: "anonymous", style: {
                    ...videoSizingStyles,
                    objectFit: 'cover',
                }, disablePictureInPicture: true, controls: false, ...videoElementProps, ref: videoRef, onPlaying: () => {
                    setIsPlaying(true);
                }, children: _jsx("source", { src: cloudFrontPreviewUrl }, videoSrc) })] }));
};
export default PlayVideoOnHover;
